import { JsCarousel } from '../../carousels/JsCarousel';
import WatchlistCta from 'components/watchlist/WatchlistCta';
import PreferencesCta from 'components/preferences/PreferencesCta';
import { getLocale } from 'services/LocalizationService';
import 'vendor/jquery.isOnScreen';
import { debounce, getCurrentPageRowPosition, dispatchEventToAllCarousels } from '../helpers';

import Highlight from '../Highlight';

// START A/B TEST PLGRWEB-1647-SearchPage-Previous Searches
import {
    initializePreviousSearchInstance,
    trackRecentSearchCarousel,
} from 'abTests/testClasses/SearchPagePreviousSearch';
// END A/B TEST PLGRWEB-1647-SearchPage-Previous Searches

const RELATED_SHOWS = 'Related Shows';
const YAML = 'You Might Also Like';
const TRACK_SHOW_CONTENT = 'trackShowContent';
const TRACK_ROW_HEADER = 'trackRowHeader';
const TRACK_HUB_ROW_HEADER = 'trackHubRowHeader';
const TRACK_CAROUSEL_SELECT = 'trackCarouselSelect';
const TRACK_SEARCH_SELECT = 'trackSearchCarouselSelect';
const TRACK_SEARCH_RESULT_SELECT = 'trackSearchResult';

export default class HighlightCarousel extends Highlight(JsCarousel) {
    static VideoContentIdCache = JSON.parse(window.localStorage.getItem('VideoContentIdCache'));
    static STATE_CAROUSEL_EVENT_TYPE = 'highlightCarouselSpreadState';

    constructor(element, options) {
        super(element, options);
        this.rawCarouselData = {};
        this.lastBreakPoint = null;
        this.currentSlidesPerView = null;
        this.carouselElement = null;
        this.isKeepWatching =
            this._wrapper.get(0).closest('.js-le-carousel').getAttribute('data-model') ===
            'keepWatching';
        this.isTopComponent = this._wrapper
            .get(0)
            .closest('.js-le-carousel')
            .parentElement.classList.contains('top-components');
        this._wrapper.on('videoDataRendered', () => this.__onCarouselLoad());
        this.__initializeSpreadState(element);
        if (options.isMyListCarousel) {
            this.__initializeMyListCarouselRefreshListener(element);
        }
    }

    __onCarouselLoad() {
        let carouselContainer = this._.querySelector('.carousel-container');
        let carouselElements = carouselContainer.querySelectorAll('.swiper-slide');
        let rawCarouselElements = this.options.xhrResult.data;
        this.__saveCarouselDataById(rawCarouselElements);
        this.__initCarouselElements(carouselElements);
        this.lastBreakPoint = this.__checkBreakpoint();
        this.currentSlidesPerView = this.lastBreakPoint.slidesPerView ?? null;
        this.__initializeSwiperEvents();
    }

    __initializeMyListCarouselRefreshListener(element) {
        if (!element.getAttribute('has-my-list-listener')) {
            element.setAttribute('has-my-list-listener', true);
            document.addEventListener(Highlight.MY_LIST_CAROUSEL_EVENT_TYPE, (event) => {
                if (this.expandedElement) {
                    this.__removeExpandedElement(this.carouselElement);
                }
                this.currentParams = null;
                this.virtualSlides = null;
                this.lastIndex = 0;
                this.lastBreakPoint = null;
                this.maxSlides = 6;
                this.rawCarouselData = {};
                this.lastBreakPoint = null;
                this.currentSlidesPerView = null;
                this.__init();
            });
        }
    }

    __initializeSpreadState(element) {
        if (!element.getAttribute('has-state-listener')) {
            element.setAttribute('has-state-listener', true);
            element.addEventListener(HighlightCarousel.STATE_CAROUSEL_EVENT_TYPE, (evt) => {
                const { toggleClassName, elementId } = evt.detail;
                this.__spreadState(toggleClassName, elementId);
            });
        }
    }

    // save all carousel element data in a map for quick retrieval
    // mainly for use with swiper paging and resize where the order of elements are lost
    __saveCarouselDataById(rawCarouselElements) {
        for (const el of rawCarouselElements) {
            let id = el.id !== null && el.id !== undefined && el.id !== '' ? el.id : el.contentId;
            this.rawCarouselData[id] = el;
            this.__getKeepWatchingShowMovieContent(el);
            this.__preloadImage(el);
        }
    }

    __preloadImage(elementData) {
        throw new Error('HighlightCarousel: Do not use parent class');
    }

    __initCarouselElements(carouselElements) {
        for (let i = 0; i < carouselElements.length; i++) {
            let carouselElement = carouselElements[i];
            let id = carouselElement.querySelector('a').id;
            if (id !== null) {
                this.__initElement(carouselElement, this.rawCarouselData[id]);
            }
        }
    }

    __createExpandedElement(carouselElement, data) {
        data.contentMyListActive = this.watchlistService.isContentInWatchlist(
            this.currentElementId,
        );
        data.contentNotificationActive = this.notificationService.isContentInNotificationList(
            data.cmsId ?? '',
        );
        const expandedElement = super.__createExpandedElement(carouselElement, data);
        const offsetClassName = this.isTopComponent ? 'top-components' : 'bottom-components';

        this.carouselElement = carouselElement;

        let parentContainer = carouselElement.closest('.js-le-carousel');
        let pageSection = document.getElementsByClassName(offsetClassName)[0];

        if (!pageSection && CBS.Registry.pageType === 'Search') {
            pageSection = document.getElementsByClassName('presearch-components')[0].hasChildNodes()
                ? document.getElementsByClassName('presearch-components')[0]
                : document.getElementsByClassName('fallback-presearch-components')[0];
        }

        let topCoordinate = pageSection.offsetTop + parentContainer.offsetTop;
        expandedElement.style.top = topCoordinate + 'px';

        return expandedElement;
    }

    __createMediaManager(data, target) {
        throw new Error('HighlightCarousel: Do not use parent class');
    }

    __removeExpandedElement(carouselElement) {
        if (!this.expandedElement) return;

        const offsetClassName = this.isTopComponent ? 'top-components' : 'bottom-components';

        let parentContainer = carouselElement.closest('.js-le-carousel');
        let pageSection = document.getElementsByClassName(offsetClassName)[0];

        if (!pageSection && CBS.Registry.pageType === 'Search') {
            pageSection = document.getElementsByClassName('presearch-components')[0].hasChildNodes()
                ? document.getElementsByClassName('presearch-components')[0]
                : document.getElementsByClassName('fallback-presearch-components')[0];
        }

        let topCoordinate = pageSection.offsetTop + parentContainer.offsetTop;
        this.expandedElement.style.top = topCoordinate + 'px';

        super.__removeExpandedElement(carouselElement);
    }

    __initializeSwiperEvents() {
        const self = this;
        this.swiper.on('slideNextTransitionStart', function () {
            if (this.visibleSlides) {
                self.__initCarouselElements(this.visibleSlides);
            }
        });

        this.swiper.on('resize', function () {
            const resize = debounce(() => self.__initialResize(this.visibleSlides), 800);
            resize();
        });

        this.swiper.on('slideNextTransitionEnd', function () {
            clearTimeout(HighlightCarousel.HandleExpansionTimeout);
        });

        this.swiper.on('slidePrevTransitionEnd', function () {
            clearTimeout(HighlightCarousel.HandleExpansionTimeout);
        });
    }

    __initialResize(slides) {
        if (this.lastBreakPoint.slidesPerView > this.currentSlidesPerView) {
            this.__initCarouselElements(slides);
            this.currentSlidesPerView = this.lastBreakPoint.slidesPerView;
        }
    }

    __spreadStateEvent(elementId, toggleClassName) {
        dispatchEventToAllCarousels(
            new CustomEvent(HighlightCarousel.STATE_CAROUSEL_EVENT_TYPE, {
                detail: {
                    toggleClassName,
                    elementId,
                },
            }),
        );

        // for marquee
        if (toggleClassName === WatchlistCta.IN_WATCHLIST_CLASS) {
            const nodes = document.querySelectorAll('#watchlist-cta-' + elementId);
            nodes.forEach((element) => {
                if (element.tagName === 'A') {
                    element.classList.remove('hovered');
                    element.classList.toggle('in_watchlist');
                }
            });
        }
    }

    __spreadState(toggleClassName, elementId) {
        if (
            this.rawCarouselData[elementId] !== null &&
            this.rawCarouselData[elementId] !== undefined
        ) {
            if (toggleClassName === PreferencesCta.NOTIFY_ON_CLASS) {
                const cta = this.expandedElement?.querySelector('#notify-cta-' + elementId);
                const _link = cta?.parentElement;
                if (_link) {
                    _link.classList.toggle('active');
                    _link.classList.toggle('notify_on');
                }
            }

            if (toggleClassName === WatchlistCta.IN_WATCHLIST_CLASS) {
                const cta = this.expandedElement?.querySelector('#watchlist-cta-' + elementId);
                const _link = cta?.parentElement;
                if (_link) {
                    _link.classList.toggle('active');
                    _link.classList.toggle('in_watchlist');
                }
            }
        }
    }

    __getTrackingEventString() {
        if (!CBS.Registry.homepage) {
            if (this.rowHeaderTitle === RELATED_SHOWS) {
                return TRACK_SHOW_CONTENT;
            } else if (this.rowHeaderTitle === YAML) {
                return TRACK_ROW_HEADER;
            } else if (CBS.Registry.pageType === 'hubs_collection') {
                return TRACK_HUB_ROW_HEADER;
            } else if (CBS.Registry.pageType.toLowerCase() === 'search') {
                if (
                    __hub?.presearchSuggestionSelected !== '' &&
                    __hub?.presearchSuggestionSelected !== undefined
                ) {
                    return TRACK_SEARCH_RESULT_SELECT;
                }
                return TRACK_SEARCH_SELECT;
            } else {
                return TRACK_CAROUSEL_SELECT;
            }
        } else {
            return TRACK_CAROUSEL_SELECT;
        }
    }

    __getTrackingSpecs(carouselElement, expandedElement) {
        const linkElement = carouselElement.querySelector('a.link');
        const sectionContainer = this._wrapper.get(0).closest('.js-le-carousel');
        const url = linkElement.getAttribute('href');
        const fathom = linkElement.getAttribute('data-fathom').split('|');
        const posColNum = carouselElement.getAttribute('data-swiper-slide-index');
        const posRowNum = getCurrentPageRowPosition(this._wrapper);
        const carouselId =
            sectionContainer.getAttribute('data-carouselid') ||
            sectionContainer.getAttribute('data-displayid');
        const carouselModel = sectionContainer.getAttribute('data-model');
        const rowHeaderTitle = sectionContainer
            .querySelector('.video-section-title')
            .innerHTML.trim();
        const video = this.mediaManager.video ?? null;
        const hasVideo = video ? true : false;
        const aaLink = linkElement.getAttribute('aa-link')?.split('|') ?? [];
        const presentationStyle = sectionContainer.getAttribute('data-presentationstyle');
        const contentBadgeLabel = aaLink[12] ?? null;

        this.rowHeaderTitle = rowHeaderTitle;

        let trackTile = {
            carouselId: carouselId,
            carouselContentType: fathom[3],
            carouselModel: carouselModel,
            carouselLink: url,
            rowHeaderTitle: rowHeaderTitle,
            spliceEnabled: hasVideo ? '1' : '0',
            posRowNum: posRowNum,
            posColNum: posColNum,
            contentLocked: aaLink[23],
            carouselPresentationStyle: presentationStyle,
            isHighlightEnabled: sectionContainer.getAttribute('data-isContentHighlightEnabled'),
            showEpisodeId: aaLink[16].toString() ?? '',
            showEpisodeTitle: aaLink[5].toString() ?? '',
            contentBadgeLabel: contentBadgeLabel,
        };

        // START Add Hub Tracking Data : PPIWEB-4553
        if (typeof __hub !== 'undefined' && __hub) {
            trackTile.hubId = __hub?.id ?? utag_data?.hubId ?? '';
            trackTile.hubPageType = __hub?.hubPageType ?? utag_data?.hubPageType ?? '';
            trackTile.hubSlug = __hub?.hubSlug ?? utag_data?.hubSlug ?? '';
            trackTile.pageType = utag_data?.pageType ?? '';
            trackTile.screenName =
                'hubPageType' in __hub && 'hubSlug' in __hub
                    ? `/hub/${__hub.hubPageType.replace('_door', '')}/${__hub.hubSlug}/`
                    : '';
        }
        // END Add Hub Tracking Data : PPIWEB-4553

        if (this.__getTrackingEventString() === TRACK_CAROUSEL_SELECT) {
            const firstSlideAaLink =
                sectionContainer
                    .querySelectorAll('.swiper-slide')[0]
                    ?.querySelector('a.link')
                    ?.getAttribute('aa-link')
                    ?.split('|') ?? [];
            trackTile.promoSpotTitle =
                firstSlideAaLink.length >= 2 && firstSlideAaLink[1] === 'promoTile'
                    ? firstSlideAaLink[0]
                    : '0';
        }

        if (presentationStyle === 'default_numeric') {
            trackTile.carouselItemTotal = aaLink[28] ?? '';
        }

        // START AB Test feature/PLGRWEB-1306-Homepage-endless-scroll-carousel
        if (this.continuousEndScroll) {
            trackTile.posColNum = aaLink[3].toString() ?? '0';
            trackTile.slideCycle = this.continuousEndScroll
                .getCarouselCycle(linkElement)
                .toString();
        }
        // END AB Test feature/PLGRWEB-1306-Homepage-endless-scroll-carousel

        if (CBS.Registry.pageType.toLowerCase() === 'search') {
            const searchWrapper = document.querySelector('#search');
            const inputBarWrapper = searchWrapper.querySelector('.search__inputBar');
            const input = inputBarWrapper.querySelector('input');

            ((trackTile.suggestedSearchTerm = __hub?.presearchSuggestionSelected ?? ''),
                (trackTile.searchTerm = input?.value ?? ''));
            trackTile.isHighlightEnabled = true;
            trackTile.searchEventComplete = 1;
        }

        const trackShowMovie =
            fathom[3] === 'show'
                ? {
                      showSeriesId: fathom[0] ? fathom[0] : linkElement.getAttribute('id'),
                      showSeriesTitle: fathom[5],
                      showGenre: aaLink[15] ?? '',
                      showSectionTitle: this.rowHeaderTitle,
                  }
                : {
                      movieId: fathom[0],
                      movieTitle: fathom[5],
                      movieGenre: aaLink[15] ?? '',
                  };

        let merged = trackTile;

        if (hasVideo) {
            const videoPreviewId = this.mediaManager.videoContentId;
            merged = { ...merged, ...{ videoPreviewId: videoPreviewId } };
        }

        const badge = expandedElement.querySelector('.new-item-container .new-item');

        if (badge) {
            merged = { ...merged, ...{ contentBadgeLabel: badge.innerHTML } };
        }

        merged = { ...merged, ...trackShowMovie };

        // Start A/B TEST PLGRWEB-1647-SearchPage-Previous Searches
        const prevSearchInstance = initializePreviousSearchInstance();
        if (prevSearchInstance) {
            merged = trackRecentSearchCarousel(merged);
        }
        // END A/B TEST PLGRWEB-1647-SearchPage-Previous Searches

        return merged;
    }

    __getKeepWatchingShowMovieContent(elementData) {
        if (!elementData.videoContentId && this.isKeepWatching) {
            let sourceId = elementData.showId;
            let id = elementData.id || elementData.contentId;
            let videoContentUrl = null;
            let cache = window.localStorage.getItem('VideoContentIdCache')
                ? new Map(JSON.parse(window.localStorage.getItem('VideoContentIdCache')))
                : new Map();

            if (cache.has(sourceId)) {
                this.rawCarouselData[id].videoContentId = cache.get(sourceId);
            } else if (sourceId != '-1') {
                if (!sourceId) {
                    sourceId = elementData.href.split('/')[3];
                    videoContentUrl = `/carousels/homepage/dynamicPlay/movieContentId/${sourceId}/`;
                } else {
                    videoContentUrl = `/carousels/homepage/dynamicPlay/showId/${sourceId}/`;
                }

                if (videoContentUrl) {
                    this._http.doGet(videoContentUrl).then(({ data }) => {
                        const spliceContentId =
                            data?.data?.spliceContentId ?? data?.data?.trailer_content_id;
                        if (spliceContentId) {
                            cache.set(sourceId, spliceContentId);
                            window.localStorage.setItem(
                                'VideoContentIdCache',
                                JSON.stringify(Array.from(cache.entries())),
                            );
                            this.rawCarouselData[id].videoContentId = spliceContentId;
                        }
                    });
                }
            }
        }
    }
}
